import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStyles } from './GenerateTTMPage.styles';

import _ from 'lodash';
import ReactJkMusicPlayer from 'react-jinke-music-player';
import 'react-jinke-music-player/assets/index.css';

import CircularProgress from '@material-ui/core/CircularProgress';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Collapse, Switch, Autocomplete, TextField, Chip, Popover, Select, MenuItem } from '@mui/material';

import ClearIcon from '@mui/icons-material/Clear';
import LoopIcon from '@mui/icons-material/Loop';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import VoiceIcon from '@mui/icons-material/RecordVoiceOver';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

import stepChecked from '../../img/step-checked.png';

import {
  fetchSubscriptionInfo,
  uploadTTMInference,
  checkTTMInferenceCompletion,
  checkTTMInferenceCompletionById,
  getTTMInferenceSignedUrl,
  getTTMCoverId,
  checkTTMPendingCover,
  checkInferenceCompletionById,
  getInferenceSignedUrl,
  getCommunitySampleSignedUrl,
  fetchTTMTags,
} from '../../services/page.services';

import { checkValidLoginStatus } from '../../utils/user.utils';
import { appActions } from '../../actions/app.actions.js';
import { userActions } from '../../actions/user.actions.js';
import SelectVoiceModal from '../layout/SelectVoiceModal';

import HtmlTooltip from '../HtmlTooltip';
import coverImg from '../../img/cover.png';
import tooltipImg from '../../img/tooltip.png';
import { labelLength, statusCheckInterval, creditsPerTtmInference } from '../../constants/app.constants.js';
import { showMessageV2 } from '../../utils/page.utils.js';

function GenerateTTMPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const tierToShowOutputFormat = ['PREMIUM', 'PRO', 'PREMIUM_YEARLY', 'PRO_YEARLY', 'ADMIN', 'CUSTOM'];
  const isAdmin = user?.subscription?.type === 'ADMIN';
  const isCustom = user?.subscription?.type === 'CUSTOM';

  const intervalIDRef = useRef(null);
  const [loadingText, setLoadingText] = useState('');
  const bottomRef = useRef(null); // ref to reference the bottom position
  const [outputFormat, setOutputFormat] = useState('mp3');

  const [tags, setTags] = useState([]);
  const [randomTags, setRandomTags] = useState([]);

  // does not require translations
  const lyricLabels = ['[Intro]', '[Verse]', '[Pre-Chorus]', '[Chorus]', '[Bridge]', '[Outro]'];

  const [promptMethod, setPromptMethod] = useState('prompt');
  const [lyricMethod, setLyricMethod] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [lyricPrompt, setLyricPrompt] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('ko');
  const [customLyrics, setCustomLyrics] = useState('');
  const [selectedModel, setSelectedModel] = useState();
  const [selectedVoiceType, setSelectedVoiceType] = useState('');
  const [instrumental, setInstrumental] = useState(false);
  const [label, setLabel] = useState('');

  const [ttmInferenceRemainingCount, setTtmInferenceRemainingcount] = useState();
  const [ttmInferenceInProgress, setTtmInferenceInProgress] = useState(false);
  const [inferenceRemainingCount, setInferenceRemainingcount] = useState();
  const [inferenceInProgress, setInferenceInProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [anchorEl, setAnchorEl] = useState();
  const lyricLabelOpen = Boolean(anchorEl);

  const [selectVoiceModalProps, setSelectVoiceModalProps] = useState({});
  const [completedAudio, setCompletedAudio] = useState();
  const [showOutputFormatTooltip, setShowOutputFormatTooltip] = useState(false);

  //play
  const [selectedAudio, setSelectedAudio] = useState();
  const [playing, setPlaying] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');

  useEffect(() => () => intervalIDRef.current && clearInterval(intervalIDRef.current), []);

  useEffect(() => {
    return () => {
      dispatch(appActions.clearSelectedCommunityItem());
    };
  }, []);

  useEffect(() => {
    if (userId) (async () => initPage())();
  }, [userId]);

  //add event listened to be triggered whenever the played song ends
  useEffect(() => {
    if (selectedAudio) {
      selectedAudio.addEventListener('ended', () => {
        setSelectedCommunityId('');
        setPlaying(false);
      });
    }
    return () => {
      if (selectedAudio) {
        selectedAudio.pause();
        selectedAudio.removeEventListener('ended', () => {
          setSelectedCommunityId('');
          setPlaying(false);
        });
      }
    };
  }, [selectedAudio]);

  // when user clicks play button
  useEffect(() => {
    if (selectedAudio) {
      playing ? selectedAudio.play() : selectedAudio.pause();
    }
  }, [playing]);

  const hasStyle = (promptMethod === 'prompt' && prompt) || (promptMethod === 'tags' && selectedTags.length > 0);
  const hasLyrics = (lyricMethod === 'generate' && lyricPrompt) || (lyricMethod === 'custom' && customLyrics);

  const readyForSubmit = (() => {
    if (instrumental) {
      // if instrumental is true, one of 'prompt' or 'tags' must be used
      return ((promptMethod === 'prompt' && prompt) || (promptMethod === 'tags' && selectedTags.length > 0)) && label;
    } else {
      // if not instrumental, one of 'prompt' or 'tags' must exist, one of 'lyricPrompt' or 'customLyrics' must exist, and 'label' must exist
      return (hasStyle || hasLyrics) && label;
    }
  })();

  const emptyParams = !instrumental ? !hasStyle && !hasLyrics : !hasStyle;

  const creditsRequired = (() => {
    if (!instrumental && selectedModel) {
      return creditsPerTtmInference + 1;
    } else {
      return creditsPerTtmInference;
    }
  })();

  // when loadingtext exists, scroll to the very bottom
  useEffect(() => {
    if (loadingText !== '') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loadingText]);

  useEffect(() => {
    if (!hasStyle && !hasLyrics && !label) {
      setCurrentStep(Math.max(1, currentStep));
    } else if (
      ((promptMethod === 'prompt' && prompt) ||
        (promptMethod === 'tags' && selectedTags.length > 0) ||
        (lyricMethod === 'generate' && lyricPrompt) ||
        (lyricMethod === 'custom' && customLyrics)) &&
      !label
    ) {
      setCurrentStep(Math.max(3, currentStep));
    }
  }, [promptMethod, prompt, selectedTags, lyricMethod, lyricPrompt, customLyrics, instrumental, label]);

  const checkAndPollPendingInference = async () => {
    const pollPendingInferences = async () => {
      let ttmInferences = await checkTTMInferenceCompletion();
      while (_.some(ttmInferences, { status: 'PENDING' })) {
        setTtmInferenceInProgress(true);
        await new Promise(resolve => setTimeout(resolve, statusCheckInterval));
        ttmInferences = await checkTTMInferenceCompletion();
      }
      setTtmInferenceInProgress(false);
    };
    const pollPendingCovers = async () => {
      let pendingCoverExists = await checkTTMPendingCover();
      while (pendingCoverExists) {
        setInferenceInProgress(true);
        await new Promise(resolve => setTimeout(resolve, statusCheckInterval));
        pendingCoverExists = await checkTTMPendingCover();
      }
      setInferenceInProgress(false);
    };
    await Promise.all([pollPendingInferences(), pollPendingCovers()]);

    await checkRemainingCount();
    setTtmInferenceInProgress(false);
    setInferenceInProgress(false);
  };

  const getBlobFromS3Url = async url => {
    const res = await fetch(url);
    return res.blob();
  };

  // shows 6 random tags from the 200 most popular tags
  const randomizeTags = async () => {
    if (tags.length > 0) {
      const popularTags = tags.slice(0, 200);
      const shuffled = [...popularTags].sort(() => 0.5 - Math.random());
      setRandomTags(shuffled.slice(0, 12));
    }
  };

  const initPage = async () => {
    try {
      if (!isAdmin && !isCustom) {
        checkAndPollPendingInference();
      }
      await checkRemainingCount();
      setTags(await fetchTTMTags());
    } catch (e) {
      showMessageV2(dispatch, t('modal.pageLoadFail'), { reloadOnClose: true });
    }
  };

  // randomize tags when the page loads
  useEffect(() => {
    if (tags.length > 0) {
      randomizeTags();
    }
  }, [tags]);

  useEffect(() => {
    setSelectedLanguage(t('languageDetector') || 'ko');
  }, [t('languageDetector')]);

  const getLocalizedTag = tag => {
    const languageMap = {
      ko: tag.korean,
      ja: tag.japanese,
      th: tag.thai,
    };
    return languageMap[t('languageDetector')] || tag.name;
  };

  const checkRemainingCount = async () => {
    let subscriptionInfo = await fetchSubscriptionInfo();
    dispatch(userActions.updateSubscription(subscriptionInfo));
    setTtmInferenceRemainingcount(
      subscriptionInfo?.freeTtmInferenceRemaining + subscriptionInfo?.genCreditsRemaining || 0
    );
    setInferenceRemainingcount(subscriptionInfo?.freeInferenceRemaining + subscriptionInfo?.genCreditsRemaining || 0);
    return subscriptionInfo;
  };

  const checkCompleteForm = () => {
    let message = '';
    if (ttmInferenceInProgress) {
      message = t('ttmInference.modal.inProgress');
    } else if (selectedModel && !instrumental && inferenceInProgress) {
      message = t('ttmInference.modal.coverInProgress');
    } else if (
      ttmInferenceRemainingCount + (selectedModel && inferenceRemainingCount) <
      creditsPerTtmInference + (selectedModel && 1)
    ) {
      message = t('ttmInference.modal.lackRemaining');
    } else if (emptyParams) {
      message = t('ttmInference.modal.emptyParams');
    } else if (label.trim().length === 0) {
      message = t('ttmInference.modal.noLabel');
    } else if (label.trim().length > labelLength.ttm) {
      message = t('ttmInference.modal.longLabel');
    }
    if (message) {
      showMessageV2(dispatch, message);
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    if (!checkCompleteForm()) return;
    setCompletedAudio(null);
    setLoadingText(t('ttmInference.submit.uploading'));
    let inference;
    try {
      const usedPrompt = promptMethod === 'prompt' ? prompt : selectedTags;
      const lyrics = lyricMethod === 'generate' ? lyricPrompt : customLyrics;

      const voiceModel = {
        id: selectedModel?.id,
        label: selectedModel?.label,
        community: selectedVoiceType === 'community',
        aihub: !!selectedModel?.applioId,
      };

      inference = await uploadTTMInference(
        label,
        usedPrompt.length > 0 ? usedPrompt : null,
        promptMethod,
        !instrumental && lyrics.length > 0 ? lyrics : null,
        !instrumental ? lyricMethod : null,
        !instrumental && lyricMethod === 'generate' ? selectedLanguage : null,
        instrumental,
        outputFormat,
        !instrumental && voiceModel.id ? voiceModel : null,
        user.subscription.type
      );
    } catch (e) {
      showMessageV2(dispatch, t('ttmInference.submit.tryAgain'));
      console.log(e);
      setLoadingText('');
      return;
    }
    checkRemainingCount();
    setLoadingText(t('ttmInference.submit.inProgress'));

    if (inference) {
      const tick = async () => {
        try {
          const id = inference.id;
          const fileName = `${id}.${inference.outputFormat}`;
          const downloadName = `${inference.label}.${inference.outputFormat}`;
          const status = await checkTTMInferenceCompletionById(id);
          setLoadingText(t('ttmInference.submit.inProgress'));
          if (status === 'COMPLETE') {
            if (!inference.voiceModel) {
              clearInterval(intervalIDRef.current);
              const songUrl = await getTTMInferenceSignedUrl(id, fileName, downloadName);
              const songBlob = await getBlobFromS3Url(songUrl);
              setLoadingText(t('ttmInference.submit.completedAndLoadingPlayer'));

              await checkRemainingCount();
              setCompletedAudio(
                _.compact([
                  {
                    name: inference.label,
                    musicSrc: URL.createObjectURL(songBlob.slice(0, songBlob.size, `audio/${inference.outputFormat}`)),
                    extension: inference.outputFormat,
                  },
                ])
              );
              setLoadingText('');
            } else {
              clearInterval(intervalIDRef.current);
              const coverId = await getTTMCoverId(id);
              const coverFileName = `${coverId}.${inference.outputFormat}`;
              const coverUrl = await getInferenceSignedUrl(coverId, coverFileName);
              const coverBlob = await getBlobFromS3Url(coverUrl);
              setLoadingText(t('ttmInference.submit.completedCoverAndLoadingPlayer'));

              await checkRemainingCount();
              setCompletedAudio(
                _.compact([
                  {
                    name: `${inference.voiceModel.label} - ${inference.label}`,
                    musicSrc: URL.createObjectURL(
                      coverBlob.slice(0, coverBlob.size, `audio/${inference.outputFormat}`)
                    ),
                    extension: inference.outputFormat,
                  },
                ])
              );
              setLoadingText('');
            }
          } else if (status === 'FAILED') {
            showMessageV2(dispatch, t('ttmInferenceTab.submit.tryAgain'));
            clearInterval(intervalIDRef.current);
            setLoadingText('');
          }
        } catch (e) {
          console.log(e);
        }
      };
      intervalIDRef.current = setInterval(tick, statusCheckInterval);
    }
  };

  const customDownloader = downloadInfo => {
    const downloadingAudio = _.find(completedAudio, {
      musicSrc: downloadInfo.src,
    });
    const link = document.createElement('a');
    link.href = downloadInfo.src;
    link.download = `${downloadingAudio.name}.${downloadingAudio.extension}`;
    document.body.appendChild(link);
    link.click();
  };

  const onBeforeDestroy = () => {
    return new Promise((_resolve, reject) => {
      setCompletedAudio(null);
      reject();
    });
  };

  const handleWavButtonClick = () => {
    if (checkValidLoginStatus(userId, dispatch)) {
      if (!tierToShowOutputFormat.includes(user.subscription?.type)) {
        setShowOutputFormatTooltip(true);
      } else {
        setOutputFormat('wav');
        setShowOutputFormatTooltip(false);
      }
    }
  };

  const handleSelectVoice = async () => {
    if (checkValidLoginStatus(userId, dispatch)) {
      setSelectVoiceModalProps({
        open: true,
        onSelectVoice: async (selectedVoice, selectedVoiceType) => {
          setSelectedModel(selectedVoice);
          setSelectedVoiceType(selectedVoiceType);
          setSelectVoiceModalProps({ open: false });
        },
        onSelectVoiceModalClose: () => {
          setSelectVoiceModalProps({ open: false });
        },
      });
    }
  };

  const handlePlay = async communityItemId => {
    if (selectedCommunityId === communityItemId) {
      setPlaying(true);
    } else {
      setPlaying(false);
      const sampleVoiceUrl = await getCommunitySampleSignedUrl(communityItemId);
      const sampleVoiceBlob = await getBlobFromS3Url(sampleVoiceUrl);
      if (sampleVoiceBlob.size > 2000) {
        const audio = new Audio(URL.createObjectURL(sampleVoiceBlob.slice(0, sampleVoiceBlob.size, `audio/mp3`)));

        setSelectedAudio(audio);
        setPlaying(true);
        setSelectedCommunityId(communityItemId);
      }
    }
  };

  const handlePlayDebounce = _.debounce(handlePlay, 500, { leading: true });

  const filterOptions = createFilterOptions({
    stringify: option => `${option.name} ${option.korean} ${option.japanese} ${option.thai}`,
  });

  const classes = useStyles();
  return (
    <>
      {selectVoiceModalProps.open && <SelectVoiceModal {...selectVoiceModalProps} />}
      <div className={classes.container}>
        <div className={classes.pageTitle}>{t('ttmInference.title')}</div>

        <div className={`${classes.stepHeader}`}>
          {hasStyle || (hasLyrics && !instrumental) ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} />
          ) : (
            <span className={classes.stepHeaderNum}>1</span>
          )}
          <span>{t('ttmInference.stepOne.title')}</span>
          <HtmlTooltip
            title={
              <div className={'globalTooltipContainer'}>
                <div className={'globalTooltipTextContainer'}>
                  <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                  <div className={'globalTooltipText'}>{t('ttmInference.stepOne.tooltip.contents.0')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                  <div className={'globalTooltipText'}>{t('ttmInference.stepOne.tooltip.contents.1')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                  <div className={'globalTooltipText'}>{t('ttmInference.stepOne.tooltip.contents.2')}</div>
                </div>
              </div>
            }
          >
            <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
          </HtmlTooltip>
        </div>
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <div className={classes.promptMethodContainer}>
              <div
                className={`${classes.promptMethod} ${promptMethod === 'prompt' && classes.selectedPromptMethod}`}
                onClick={() => setPromptMethod('prompt')}
              >
                {t('ttmInference.stepOne.promptMethod.prompt')}
              </div>
              <div
                className={`${classes.promptMethod} ${promptMethod === 'tags' && classes.selectedPromptMethod}`}
                onClick={() => setPromptMethod('tags')}
              >
                {t('ttmInference.stepOne.promptMethod.tags')}
              </div>
              <HtmlTooltip
                className={classes.compactTooltip}
                title={
                  <div className={'globalTooltipContainer'}>
                    <div className={'globalTooltipHeader'}>{t('ttmInference.stepOne.promptMethod.tooltip.title')}</div>
                    <div className={'globalTooltipTextContainer'}>
                      <div className={'globalTooltipText'}>
                        {t('ttmInference.stepOne.promptMethod.tooltip.contents.0')}
                      </div>
                    </div>
                    <div className={'globalTooltipTextContainer'}>
                      <div className={'globalTooltipText'}>
                        {t('ttmInference.stepOne.promptMethod.tooltip.contents.1')}
                      </div>
                    </div>
                    <div className={'globalTooltipTextContainer'}>
                      <div className={'globalTooltipText'}>
                        {t('ttmInference.stepOne.promptMethod.tooltip.contents.2')}
                      </div>
                    </div>
                  </div>
                }
              >
                <img className={`${classes.tooltipImg} ${classes.noMargin}`} src={tooltipImg} alt="tooltip-img" />
              </HtmlTooltip>
            </div>
            <div className={classes.switchContainer}>
              {t('ttmInference.stepOne.instrumental')}
              <Switch
                disableRipple
                size="small"
                className={classes.switch}
                onClick={() => {
                  if (checkValidLoginStatus(userId, dispatch)) {
                    setInstrumental(!instrumental);
                  }
                }}
              />
            </div>
          </div>
          {promptMethod === 'prompt' ? (
            <input
              className={classes.textInput}
              placeholder={t(`ttmInference.stepOne.placeholder.prompt`)}
              maxLength={500}
              value={prompt}
              onChange={e => {
                if (checkValidLoginStatus(userId, dispatch)) {
                  setPrompt(e.target.value);
                }
              }}
            />
          ) : (
            <>
              <Autocomplete
                multiple
                size="small"
                className={classes.autocomplete}
                // need to override list style with a separate class
                slotProps={{
                  paper: { className: classes.autocompletePaper },
                  listbox: { className: classes.autocompleteList },
                }}
                noOptionsText={t('ttmInference.stepOne.noOptions')}
                options={tags}
                filterOptions={filterOptions}
                value={selectedTags.map(tagName => tags.find(tag => tag.name === tagName))}
                onChange={(_, options) => {
                  setSelectedTags(options.map(option => option.name));
                }}
                getOptionLabel={option => option.name}
                renderInput={params => (
                  <TextField {...params} placeholder={t('ttmInference.stepOne.placeholder.tags')} />
                )}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id}>
                      {getLocalizedTag(option)}
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={option.id} label={getLocalizedTag(option)} size="small" {...tagProps} />;
                  })
                }
              />
              <div className={classes.randomTagsOuterContainer}>
                <LoopIcon onClick={() => randomizeTags()} color="inherit" />
                <div className={classes.randomTagsContainer}>
                  {randomTags.map((tag, index) => (
                    <div
                      key={index}
                      className={`${classes.tag} ${selectedTags.includes(tag.name) && classes.disabled}`}
                      onClick={() => {
                        if (checkValidLoginStatus(userId, dispatch)) {
                          !selectedTags.includes(tag.name) && setSelectedTags(prev => [...prev, tag.name]);
                        }
                      }}
                    >
                      {getLocalizedTag(tag)}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <Collapse in={!instrumental} timeout="auto" unmountOnExit>
          <div className={classes.section}>
            <div className={classes.sectionHeader}>
              <div className={classes.promptMethodContainer}>
                <div
                  className={`${classes.promptMethod} ${lyricMethod === 'generate' && classes.selectedPromptMethod}`}
                  onClick={() => setLyricMethod('generate')}
                >
                  {t('ttmInference.stepOne.lyricMethod.generate')}
                </div>
                <div
                  className={`${classes.promptMethod} ${lyricMethod === 'custom' && classes.selectedPromptMethod}`}
                  onClick={() => setLyricMethod('custom')}
                >
                  {t('ttmInference.stepOne.lyricMethod.custom')}
                </div>
                <HtmlTooltip
                  className={classes.compactTooltip}
                  title={
                    <div className={'globalTooltipContainer'}>
                      <div className={'globalTooltipHeader'}>{t('ttmInference.stepOne.lyricMethod.tooltip.title')}</div>
                      <div className={'globalTooltipTextContainer'}>
                        <div className={'globalTooltipText'}>
                          {t('ttmInference.stepOne.lyricMethod.tooltip.contents.0')}
                        </div>
                      </div>
                      <div className={'globalTooltipTextContainer'}>
                        <div className={'globalTooltipText'}>
                          {t('ttmInference.stepOne.lyricMethod.tooltip.contents.1')}
                        </div>
                      </div>
                      <div className={'globalTooltipTextContainer'}>
                        <div className={'globalTooltipText'}>
                          {t('ttmInference.stepOne.lyricMethod.tooltip.contents.2')}
                        </div>
                      </div>
                    </div>
                  }
                >
                  <img className={`${classes.tooltipImg} ${classes.noMargin}`} src={tooltipImg} alt="tooltip-img" />
                </HtmlTooltip>
              </div>
              {lyricMethod === 'generate' && (
                <Select
                  value={selectedLanguage}
                  onChange={e => {
                    if (checkValidLoginStatus(userId, dispatch)) {
                      setSelectedLanguage(e.target.value);
                    }
                  }}
                  className={classes.languageSelect}
                  MenuProps={{
                    disableScrollLock: true,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'right',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'right',
                    },
                    slotProps: {
                      paper: {
                        sx: {
                          marginTop: '6px!important',
                          border: '1px solid #404040!important',
                          '& .MuiList-root': {
                            '& .MuiMenuItem-root': {
                              padding: '0 12px!important',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="en" dense>
                    EN
                  </MenuItem>
                  <MenuItem value="ko" dense>
                    한국어
                  </MenuItem>
                  <MenuItem value="ja" dense>
                    日本語
                  </MenuItem>
                  <MenuItem value="th" dense>
                    ภาษาไทย
                  </MenuItem>
                </Select>
              )}
            </div>
            {lyricMethod === 'generate' ? (
              <input
                className={classes.textInput}
                placeholder={t('ttmInference.stepOne.placeholder.generateLyrics')}
                maxLength={500}
                value={lyricPrompt}
                onChange={e => {
                  if (checkValidLoginStatus(userId, dispatch)) {
                    setLyricPrompt(e.target.value);
                  }
                }}
              />
            ) : (
              <div className={classes.textAreaContainer}>
                <textarea
                  className={classes.textArea}
                  placeholder={t('ttmInference.stepOne.placeholder.customLyrics')}
                  maxLength={3000}
                  rows={12}
                  value={customLyrics}
                  onChange={e => {
                    if (checkValidLoginStatus(userId, dispatch)) {
                      setCustomLyrics(e.target.value);
                    }
                  }}
                />
                <div className={classes.textAreaFooter}>
                  <div className={classes.textAreaCount}>{customLyrics.length} / 3000</div>
                  <div className={classes.lyricLabelButton} onClick={e => setAnchorEl(e.currentTarget)}>
                    <PlaylistAddIcon className={classes.lyricLabelIcon} />
                  </div>
                </div>
                <Popover
                  open={lyricLabelOpen}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  className={classes.popover}
                  elevation={0}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  disableScrollLock
                >
                  <div className={classes.lyricLabelContainer}>
                    {lyricLabels.map((label, index) => (
                      <div
                        key={index}
                        className={classes.lyricLabel}
                        onClick={() => {
                          const labelText = `${label}\n\n`;
                          setCustomLyrics(prev => prev + labelText);
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </Popover>
              </div>
            )}
          </div>
        </Collapse>
        <div className={`${classes.stepHeader} ${currentStep < 2 && classes.greyout}`}>
          {currentStep >= 2 ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} />
          ) : (
            <span className={classes.stepHeaderNum}>2</span>
          )}
          <span>{t('ttmInference.stepTwo.title')}</span>
          {!tierToShowOutputFormat.includes(user.subscription?.type) && <div className={classes.proTag}>PRO+</div>}
        </div>
        <Collapse in={currentStep >= 2} timeout="auto" unmountOnExit>
          {selectedModel ? (
            <div className={`${classes.card} ${instrumental && classes.disabled}`} key={selectedModel.id}>
              <img
                className={classes.coverImg}
                src={selectedModel.image ? selectedModel.image : coverImg}
                alt="cover-img"
              />
              <div className={classes.cardSecondColumn}>
                <div className={classes.cardLabel}>{selectedModel.label}</div>
                <div className={classes.cardUsername}>{selectedModel.username || ''}</div>

                <div className={classes.cardDetails}>
                  {!selectedModel.liked && <FavoriteBorderRoundedIcon className={classes.unLikedImg} />}
                  {selectedModel.liked && <FavoriteIcon className={classes.likedImg} />}
                  <div className={classes.numberLikes}>
                    {selectedModel.likedNumber ? selectedModel.likedNumber : ''}
                  </div>
                  <div className={classes.cardDetailSeparator}>•</div>
                  <div>{selectedModel.numberUsed} creations</div>
                </div>
              </div>

              <div className={classes.playButtonGrid}>
                <ClearIcon
                  onClick={() => {
                    setSelectedCommunityId('');
                    setPlaying(false);
                    setSelectedModel(null);
                  }}
                  className={classes.clearButtonImg}
                />
                {!selectedModel.applioId && (
                  <>
                    {playing && selectedModel.id === selectedCommunityId ? (
                      <PauseCircleOutlineIcon
                        className={classes.playButtonImg}
                        onClick={event => {
                          event.stopPropagation();
                          setPlaying(false);
                        }}
                      />
                    ) : (
                      <PlayCircleOutlineIcon
                        className={classes.playButtonImg}
                        onClick={event => {
                          event.stopPropagation();
                          handlePlayDebounce(selectedModel.id);
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`${classes.selectVoiceButton} ${!tierToShowOutputFormat.includes(user.subscription?.type) || instrumental ? classes.disabled : ''}`}
              onClick={() => {
                if (checkValidLoginStatus(userId, dispatch)) {
                  !instrumental && tierToShowOutputFormat.includes(user.subscription?.type) && handleSelectVoice();
                }
              }}
            >
              <div className={classes.selectVoiceButtonText}>
                <VoiceIcon /> {t('ttmInference.stepTwo.selectVoice')}
              </div>
              <HtmlTooltip
                className={classes.compactTooltip}
                title={
                  <div className={'globalTooltipContainer'}>
                    <div className={'globalTooltipTextContainer'}>
                      <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                      <div className={'globalTooltipText'}>{t('ttmInference.stepTwo.tooltip.contents.0')}</div>
                    </div>
                  </div>
                }
              >
                <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
              </HtmlTooltip>
            </div>
          )}
        </Collapse>
        <div className={`${classes.stepHeader} ${currentStep < 3 && classes.greyout}`}>
          {label ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} />
          ) : (
            <span className={classes.stepHeaderNum}>3</span>
          )}
          <span>{t('ttmInference.stepThree.title')}</span>
        </div>
        <Collapse in={currentStep >= 3} timeout="auto" unmountOnExit>
          <div className={`${classes.section}`}>
            <input
              onChange={e => {
                if (checkValidLoginStatus(userId, dispatch)) {
                  setLabel(e.target.value);
                }
              }}
              placeholder={t('ttmInference.stepThree.placeholder')}
              className={classes.textInput}
              value={label}
            />
            <div className={classes.outputButtonContainer}>
              <div
                className={`${classes.extensionButton} ${outputFormat == 'mp3' ? classes.extensionSelected : ''}`}
                onClick={() => setOutputFormat('mp3')}
              >
                {'MP3'}
              </div>
              <HtmlTooltip
                title={
                  <div className={'globalTooltipContainer'}>
                    <div className={'globalTooltipTextContainer'}>
                      <div className={'globalTooltipText'}>
                        {t('ttmInference.stepThree.tooltip.outputFormatMessage')}
                      </div>
                    </div>
                    <div className={'globalTooltipTextContainer'}>
                      <Link to="/subscription" className={'globalTooltipTextLink'}>
                        {user.subscription?.type.startsWith('BASIC')
                          ? t('ttmInference.stepThree.tooltip.upgradePrompt')
                          : t('ttmInference.stepThree.tooltip.subscribePrompt')}
                      </Link>
                    </div>
                  </div>
                }
                interactive
                leaveDelay={200}
                open={showOutputFormatTooltip && !tierToShowOutputFormat.includes(user.subscription?.type)}
                onClose={() => setShowOutputFormatTooltip(false)}
              >
                <div
                  className={`${classes.extensionButton} ${outputFormat === 'wav' ? classes.extensionSelected : ''}`}
                  onClick={handleWavButtonClick}
                >
                  {'WAV'}
                </div>
              </HtmlTooltip>
            </div>
          </div>
        </Collapse>
        <div className={classes.separater} />
        <div className={classes.submitContainer}>
          {loadingText ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size="1.4rem" />
              <span className={classes.loadingText}>{loadingText}</span>
            </div>
          ) : (
            <>
              <div className={classes.submitInfo}>
                <div className={classes.creditUsageText}>
                  {creditsRequired} {t('ttmInference.submit.creditUsage')}
                </div>
                <div>{t('ttmInference.submit.info.0')}</div>
                <div>{t('ttmInference.submit.info.1')}</div>
              </div>
              {ttmInferenceInProgress ? (
                <div className={classes.progressButton}>
                  <>
                    <div>{t('ttmInference.submit.alreadyInProgress')}</div>
                    <CircularProgress className={classes.loadingCircle} />
                  </>
                </div>
              ) : (
                <div
                  className={`${classes.submitButton} ${!readyForSubmit && classes.disabledButton}`}
                  onClick={handleSubmit}
                >
                  {userId && ttmInferenceRemainingCount < 2
                    ? t('ttmInference.submit.lackRemaining')
                    : t('ttmInference.submit.start')}
                </div>
              )}
            </>
          )}
        </div>
        <div ref={bottomRef} />
        {completedAudio && (
          <ReactJkMusicPlayer
            mode="full"
            autoPlay={false}
            showPlayMode={false}
            showThemeSwitch={false}
            showMiniModeCover={false}
            autoHiddenCover
            onBeforeDestroy={onBeforeDestroy}
            spaceBar={true}
            showDestroy={true}
            responsive={false}
            showReload={false}
            toggleMode={false}
            remove={false}
            customDownloader={customDownloader}
            audioLists={completedAudio}
            clearPriorAudioLists
          />
        )}
      </div>
    </>
  );
}

export default GenerateTTMPage;
